import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { PURGE } from "redux-persist";
import { subscriptionApi } from "../../apis/subscription";
import { ModifyAnnuallySubscription, SubscriptionCreditType } from "./type";

interface SubscriptionInitialState {
  // 연결제 플랜 변경
  modifyAnnuallySubscription: ModifyAnnuallySubscription | null;
  // 구독
  subscriptionCredit: SubscriptionCreditType | null;
  // 엔터프라이즈 문의 여부
  isEnterpriseInquiries: boolean;
  // 엔터프라이즈 플랜 여부
  isEnterprisePlan: boolean;
  // 자동결제 실패 여부
  isPaymentFail: boolean;
  // 기업 구독 여부
  isCompanySubscription: boolean;
}

const initialState: SubscriptionInitialState = {
  modifyAnnuallySubscription: null,
  subscriptionCredit: null,
  isEnterpriseInquiries: false,
  isEnterprisePlan: false,
  isPaymentFail: false,
  isCompanySubscription: false,
};

export const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    changeAnnuallySubscription: (
      state,
      action: PayloadAction<ModifyAnnuallySubscription | null>,
    ) => {
      state.modifyAnnuallySubscription = action.payload;
    },
    changeSubscriptionCredit: (
      state,
      action: PayloadAction<SubscriptionCreditType | null>,
    ) => {
      state.subscriptionCredit = action.payload;
    },
    closeEnterprisePlanDialog: (state) => {
      state.isEnterpriseInquiries = false;
      state.isEnterprisePlan = false;
    },
    closePaymentFailDialog: (state) => {
      state.isPaymentFail = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
    builder.addMatcher(
      subscriptionApi.endpoints.getCurrentSubscription.matchFulfilled,
      (state, { payload }) => {
        if (payload) {
          // 시간, 분, 초까지 포함한 정확한 만료 체크
          // subscriptionEndAtTo가 없을 경우 만료로 처리 (안전성)
          const isExpired = payload.subscriptionEndAtTo
            ? dayjs().isAfter(dayjs(payload.subscriptionEndAtTo))
            : true;

          if (payload.subscriptionStatus === "PAYMENT_FAIL") {
            // PAYMENT_FAIL은 무조건 구독 없음
            state.isCompanySubscription = false;
            state.subscriptionCredit = null;
            state.isPaymentFail = true;
          } else if (!isExpired) {
            // 아직 만료 시간이 안 지났으면 구독 유효
            state.isCompanySubscription = true;
            state.subscriptionCredit = {
              numberOfLoadingCode: payload.planVersion.numberOfLoadingCode,
              numberOfMember: payload.planVersion.numberOfMember,
              memberNumberCredit: payload.memberNumberCredit,
            };
            state.isPaymentFail = false;
          } else {
            // 만료 시간이 지났으면 구독 없음
            state.isCompanySubscription = false;
            state.subscriptionCredit = null;
            state.isPaymentFail = false;
          }
        }
      },
    );

    builder.addMatcher(
      subscriptionApi.endpoints.getEnterpriseInquiries.matchFulfilled,
      (state, { payload }) => {
        const { rows } = payload;
        const currentInquiries = rows[0];

        if (payload.statusCode === 200 && currentInquiries) {
          state.isEnterpriseInquiries = true;
        }
      },
    );

    builder.addMatcher(
      subscriptionApi.endpoints.getPlans.matchFulfilled,
      (state, { payload }) => {
        const { rows } = payload;
        const enterprisePlans = rows.filter(
          (item) => item.planType === "ENTERPRISE" && item.isActivated,
        );

        if (payload.statusCode === 200 && enterprisePlans.length >= 1) {
          state.isEnterprisePlan = true;
        }
      },
    );

  },
});

export default subscriptionSlice.reducer;
export const {
  changeAnnuallySubscription,
  changeSubscriptionCredit,
  closeEnterprisePlanDialog,
  closePaymentFailDialog,
} = subscriptionSlice.actions;
